import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, ComposedChart, Line, Legend, Area, ReferenceLine } from 'recharts';

interface WinningContent {
  caption: string;
  type: string;
  reach: number;
  likes: number;
  comments: number;
  saves: number;
  shares: number;
  permalink: string;
  date: string;
}

interface AvailableMetric {
  id: string;
  name: string;
  category: string;
  color: string;
}

interface TimeseriesPoint {
  date: string;
  date_full: string;
  leads: number;
  spend: number;
  impressions: number;
  clicks: number;
  cpm: number;
  ctr: number;
  cpl: number;
  stories_count: number;
  stories_reach: number;
  organic_reach: number;
  organic_engagement: number;
  posts: number;
  engagement_rate: number;
  followers: number;
  follower_delta: number;
  account_reach: number;
  [key: string]: string | number;
}

interface WarRoomData {
  timestamp: string;
  ads: {
    cpm_global: number;
    ctr_global: number;
    spend_total_today: number;
    impressions_total: number;
    clicks_total: number;
    cpm_breakdown: Array<{
      account: string;
      cpm: number;
      spend: number;
      impressions: number;
    }>;
    cpm_avg_7d: number;
    cpm_delta_pct: number;
    cpm_alert: boolean;
  };
  leads: {
    count_today: number;
    count_7d: number;
    cpl_realtime: number;
    cpl_label: string;
    cpl_by_hour: Array<{
      hour: string;
      cpl: number;
      leads: number;
      spend: number;
    }>;
    cpl_scalable: boolean;
    correlation_data: Array<{
      date: string;
      leads: number;
      stories_views: number;
      stories_count: number;
      posts: number;
      post_caption: string;
    }>;
  };
  instagram: {
    stories_retention: number;
    stories_total_7d: number;
    organic_pulse: number;
    engagement_rate: number;
    follower_velocity: number;
    temperature: number;
    followers_count: number;
  };
  winning_content: WinningContent | null;
  alerts: {
    cpm_high: boolean;
    cpl_scalable: boolean;
  };
  timeseries_data: TimeseriesPoint[];
  available_metrics: AvailableMetric[];
}

function useInterval(callback: () => void, delayMs: number | null) {
  useEffect(() => {
    if (delayMs === null) return;
    const id = setInterval(callback, delayMs);
    return () => clearInterval(id);
  }, [callback, delayMs]);
}

/* Custom Tooltip for Correlation Chart */
const CorrelationTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  const dataPoint = payload[0]?.payload;
  return (
    <div style={{
      background: '#1a1a2e', border: '1px solid #00ffcc', borderRadius: '8px',
      padding: '0.8rem 1rem', maxWidth: '280px', fontFamily: 'Courier New, monospace'
    }}>
      <div style={{ color: '#00ffcc', fontWeight: 'bold', marginBottom: '0.4rem', fontSize: '0.85rem' }}>
        📅 {label}
      </div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.color || '#ccc', fontSize: '0.8rem', marginBottom: '0.2rem' }}>
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
      {dataPoint?.post_caption && (
        <div style={{
          marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #333',
          color: '#aaa', fontSize: '0.7rem', fontStyle: 'italic', lineHeight: '1.3'
        }}>
          📝 "{dataPoint.post_caption}"
        </div>
      )}
      {dataPoint?.stories_count > 0 && (
        <div style={{ color: '#3b82f6', fontSize: '0.7rem', marginTop: '0.3rem' }}>
          📱 {dataPoint.stories_count} stories postados
        </div>
      )}
    </div>
  );
};

const WarRoomDashboard: React.FC = () => {
  const [data, setData] = useState<WarRoomData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['leads', 'spend']);
  const [timeRange, setTimeRange] = useState<number>(7);
  const [cardRange, setCardRange] = useState<number>(7);

  const toggleMetric = (metricId: string) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(m => m !== metricId);
      }
      if (prev.length >= 4) return prev; // max 4 métricas simultâneas
      return [...prev, metricId];
    });
  };

  const fetchWarRoomData = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(`/api/api/v1/war-room/summary?days=${timeRange}`);
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const json = await response.json();
      setData(json);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (err: any) {
      console.error('War Room fetch error:', err);
      setError(err.message || 'Erro desconhecido');
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchWarRoomData();
  }, [fetchWarRoomData]);

  useInterval(fetchWarRoomData, 15 * 60 * 1000);

  if (loading && !data) {
    return (
      <div className="war-room-loading">
        <div className="cyber-spinner"></div>
        <p>INICIALIZANDO WAR ROOM...</p>
        <style>{`
          .war-room-loading {
            display: flex; flex-direction: column; justify-content: center;
            align-items: center; min-height: 100vh; background: #0a0a0a;
            color: #00ff41; font-family: 'Courier New', monospace;
          }
          .cyber-spinner {
            border: 4px solid #333; border-top: 4px solid #00ff41;
            border-radius: 50%; width: 60px; height: 60px;
            animation: spin 1s linear infinite; margin-bottom: 1rem;
          }
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        alignItems: 'center', minHeight: '100vh', background: '#0a0a0a',
        color: '#00ff41', fontFamily: 'Courier New, monospace'
      }}>
        <h2>⚠️ ERRO NO SISTEMA</h2>
        <p>{error}</p>
        <button onClick={fetchWarRoomData} style={{
          background: '#00ff41', color: '#0a0a0a', border: 'none',
          padding: '0.8rem 2rem', fontSize: '1rem', fontWeight: 'bold',
          cursor: 'pointer', marginTop: '1rem', borderRadius: '4px'
        }}>TENTAR NOVAMENTE</button>
      </div>
    );
  }

  if (!data) return null;

  /* Temperature gauge: 0-100 scale */
  const temp = data.instagram.temperature;
  const tempColor = temp >= 60 ? '#00ff41' : temp >= 30 ? '#ffeb3b' : '#ff073a';
  const tempLabel = temp >= 60 ? 'CRESCIMENTO' : temp >= 30 ? 'ESTÁVEL' : 'DECLÍNIO';
  const tempGaugeData = [{ name: 'Temperature', value: temp, fill: tempColor }];

  /* CPL display */
  const cplValue = data.leads.cpl_realtime;
  const cplLabel = data.leads.cpl_label || 'hoje';

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', padding: '2rem', fontFamily: 'Courier New, monospace', color: '#e0e0e0' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2.5rem', color: '#00ff41', letterSpacing: '4px', textTransform: 'uppercase' }}>
          ⚡ WAR ROOM: GROWTH & CPM
        </h1>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#00ff41', display: 'inline-block', animation: 'pulse 2s infinite' }}></span>
          <span style={{ color: '#888', fontSize: '0.9rem' }}>
            AO VIVO • Atualizado: {lastUpdate ? lastUpdate.toLocaleTimeString('pt-BR') : '—'}
          </span>
        </div>
      </div>

      {/* Metrics Grid — 5 cards: CPM, CTR, Gasto, CPL, Pulso Orgânico */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {/* CPM */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.2rem' }}>
          <div style={{ color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            CPM {data.ads.ads_date_label === 'hoje' ? 'Global' : `(${data.ads.ads_date_label})`}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00ffcc' }}>R$ {data.ads.cpm_global.toFixed(2)}</div>
          <div style={{ color: '#555', fontSize: '0.7rem', marginTop: '0.3rem' }}>
            Custo por 1.000 impressões
          </div>
        </div>

        {/* CTR */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.2rem' }}>
          <div style={{ color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            CTR {data.ads.ads_date_label === 'hoje' ? 'Global' : `(${data.ads.ads_date_label})`}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00ffcc' }}>
            {data.ads.ctr_global.toFixed(2)}%
          </div>
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            {(data.ads.clicks_total || 0).toLocaleString('pt-BR')} cliques / {((data.ads.impressions_total || 0) / 1000).toFixed(1)}k imp
          </div>
        </div>

        {/* Gasto */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.2rem' }}>
          <div style={{ color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            Gasto {data.ads.ads_date_label === 'hoje' ? 'Total Hoje' : `(${data.ads.ads_date_label})`}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff9500' }}>R$ {data.ads.spend_total_today.toFixed(2)}</div>
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            {(data.ads.impressions_total || 0).toLocaleString('pt-BR')} impressões
          </div>
        </div>

        {/* CPL */}
        <div style={{ background: '#1e1e1e', border: cplLabel !== 'hoje' ? '1px solid #ff9500' : '1px solid #333', borderRadius: '8px', padding: '1.2rem' }}>
          <div style={{ color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            CPL {cplLabel === 'hoje' ? 'Tempo Real' : `(${cplLabel})`}
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: cplValue > 0 ? '#ff00ff' : '#666' }}>
            {cplValue > 0 ? `R$ ${cplValue.toFixed(2)}` : '—'}
          </div>
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            {data.leads.count_today} leads hoje · {data.leads.count_7d} em 7d
          </div>
          {cplLabel !== 'hoje' && cplLabel !== 'sem dados' && (
            <div style={{ color: '#ff9500', fontSize: '0.65rem', marginTop: '0.3rem' }}>
              ⚠ Usando {cplLabel} (0 leads hoje)
            </div>
          )}
        </div>

        {/* Pulso Orgânico */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.2rem' }}>
          <div style={{ color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Pulso Orgânico</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00ff41' }}>
            {(data.instagram.organic_pulse || 0).toLocaleString('pt-BR')}
          </div>
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            engajamento {data.instagram.engagement_rate || 0}%
          </div>
        </div>
      </div>

      {/* ── KPI CARDS: LEADS / SEGUIDORES / STORIES / POSTS ── */}
      {data.timeseries_data && data.timeseries_data.length > 0 && (() => {
        const ts = data.timeseries_data || [];
        const sliced = ts.slice(-cardRange);
        const totalLeads = sliced.reduce((s: number, p: any) => s + (p.leads || 0), 0);
        const totalStories = sliced.reduce((s: number, p: any) => s + (p.stories_count || 0), 0);
        const totalPosts = sliced.reduce((s: number, p: any) => s + (p.posts || 0), 0);
        const realFollowers = sliced.filter((p: any) => p.followers && p.followers > 0);
        const latestFollowers = realFollowers.length > 0 ? realFollowers[realFollowers.length - 1].followers : 0;
        const firstFollowers = realFollowers.length > 0 ? realFollowers[0].followers : 0;
        const followerGrowth = latestFollowers - firstFollowers;

        const kpiCards = [
          { label: 'Leads', value: totalLeads.toLocaleString('pt-BR'), sub: `${(totalLeads / cardRange).toFixed(1)}/dia`, color: '#ff00ff', icon: '🎯' },
          { label: 'Seguidores', value: latestFollowers.toLocaleString('pt-BR'), sub: `${followerGrowth >= 0 ? '+' : ''}${followerGrowth.toLocaleString('pt-BR')} no período`, color: '#8b5cf6', icon: '👥' },
          { label: 'Stories', value: totalStories.toLocaleString('pt-BR'), sub: `${(totalStories / cardRange).toFixed(1)}/dia`, color: '#3b82f6', icon: '📸' },
          { label: 'Posts/Reels', value: totalPosts.toLocaleString('pt-BR'), sub: `${(totalPosts / cardRange).toFixed(1)}/dia`, color: '#ff4444', icon: '📹' },
        ];

        return (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h3 style={{ color: '#00ffcc', margin: 0, textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '2px' }}>
                📊 Resumo do Período
              </h3>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                {([7, 14, 30, 90] as const).map((d) => (
                  <button
                    key={`cr-${d}`}
                    onClick={() => {
                      setCardRange(d);
                      if (d > timeRange) setTimeRange(d);
                    }}
                    style={{
                      background: cardRange === d ? '#00ffcc22' : '#0d0d0d',
                      border: `1px solid ${cardRange === d ? '#00ffcc' : '#444'}`,
                      color: cardRange === d ? '#00ffcc' : '#888',
                      borderRadius: '4px',
                      padding: '0.2rem 0.55rem',
                      fontSize: '0.7rem',
                      cursor: 'pointer',
                      fontFamily: 'monospace',
                      transition: 'all 0.15s',
                    }}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
              {kpiCards.map((card) => (
                <div key={card.label} style={{
                  background: '#1e1e1e', border: `1px solid ${card.color}33`, borderRadius: '8px',
                  padding: '1.2rem', textAlign: 'center',
                  borderTop: `3px solid ${card.color}`
                }}>
                  <div style={{ fontSize: '1.4rem', marginBottom: '0.3rem' }}>{card.icon}</div>
                  <div style={{ color: '#888', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '1px' }}>{card.label}</div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: card.color }}>{card.value}</div>
                  <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.3rem' }}>{card.sub}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Charts Row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        

        {/* Correlação */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.5rem' }}>
          <h3 style={{ color: '#00ffcc', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
            📈 Correlação: Leads × Stories × Posts (7d)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={data.leads.correlation_data || []}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#888" />
              <YAxis yAxisId="left" stroke="#ff00ff" allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" allowDecimals={false} />
              <Tooltip content={<CorrelationTooltip />} />
              <Legend />
              {/* Marcadores de domingo */}
              {(() => {
                const nowM = new Date().getMonth() + 1;
                const nowY = new Date().getFullYear();
                return (data.leads.correlation_data || []).map((p: any) => p.date).filter((dateStr: string) => {
                  const parts = dateStr.split('-');
                  const mm = parseInt(parts[0]);
                  const dd = parseInt(parts[1]);
                  const year = mm > nowM ? nowY - 1 : nowY;
                  return new Date(year, mm - 1, dd).getDay() === 0;
                }).map((d: string) => (
                  <ReferenceLine
                    key={`sun-corr-${d}`}
                    x={d}
                    yAxisId="left"
                    stroke="#ffffff"
                    strokeOpacity={0.12}
                    strokeWidth={16}
                    label={{ value: 'Dom', position: 'insideTop', offset: 6, fontSize: 9, fill: '#777', fontFamily: 'monospace' }}
                  />
                ));
              })()}
              <Bar yAxisId="right" dataKey="stories_count" name="Stories Postados" barSize={22} fill="#3b82f6" opacity={0.7} radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="posts" name="Posts/Reels" barSize={18} fill="#ff4444" opacity={0.6} radius={[4, 4, 0, 0]} />
              <Line yAxisId="left" type="monotone" dataKey="leads" name="Leads" stroke="#ff00ff" strokeWidth={3} dot={{ stroke: '#ff00ff', strokeWidth: 2, r: 5, fill: '#1e1e1e' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── MULTI-METRIC CHART ────────────────────────── */}
      {data.timeseries_data && data.timeseries_data.length > 0 && (
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <h3 style={{ color: '#00ffcc', margin: 0, textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
              📊 Análise Multi-Métrica ({timeRange} Dias)
            </h3>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {([7, 14, 30, 90] as const).map((d) => (
                <button
                  key={d}
                  onClick={() => setTimeRange(d)}
                  style={{
                    background: timeRange === d ? '#00ffcc22' : '#0d0d0d',
                    border: `1px solid ${timeRange === d ? '#00ffcc' : '#444'}`,
                    color: timeRange === d ? '#00ffcc' : '#888',
                    borderRadius: '4px',
                    padding: '0.25rem 0.65rem',
                    fontSize: '0.72rem',
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    transition: 'all 0.15s',
                  }}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          <div style={{ color: '#666', fontSize: '0.7rem', marginBottom: '1rem' }}>
            Selecione até 4 métricas para comparar · Eixo esquerdo: 1ª métrica · Eixo direito: 2ª métrica
          </div>

          {/* Metric selector */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.2rem' }}>
            {(data.available_metrics || []).map((m) => {
              const isSelected = selectedMetrics.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggleMetric(m.id)}
                  style={{
                    background: isSelected ? m.color + '22' : '#0d0d0d',
                    border: `1px solid ${isSelected ? m.color : '#333'}`,
                    color: isSelected ? m.color : '#666',
                    borderRadius: '4px',
                    padding: '0.3rem 0.7rem',
                    fontSize: '0.72rem',
                    cursor: 'pointer',
                    fontFamily: 'Courier New, monospace',
                    transition: 'all 0.2s',
                  }}
                >
                  {isSelected ? '● ' : '○ '}{m.name}
                </button>
              );
            })}
          </div>

          {/* Multi-axis chart */}
          {selectedMetrics.length > 0 && (() => {
            const metricsMap = Object.fromEntries((data.available_metrics || []).map(m => [m.id, m]));
            const leftMetric = metricsMap[selectedMetrics[0]];
            const rightMetric = selectedMetrics.length > 1 ? metricsMap[selectedMetrics[1]] : null;

            const monthNamesShort = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
            const monthNamesFull = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

            const xTickFormatter = (value: string) => {
              const parts = value.split('-');
              const mm = parseInt(parts[0]);
              const dd = parseInt(parts[1]);
              if (timeRange >= 30 && dd === 1) {
                return `${monthNamesShort[mm]} 1`;
              }
              return `${dd}`;
            };

            const xInterval = timeRange >= 90 ? 6 : timeRange >= 30 ? 2 : 0;

            // Detectar domingos no timeseries
            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth() + 1;
            const sundayDates = (data.timeseries_data || []).map(p => p.date).filter((dateStr: string) => {
              const parts = dateStr.split('-');
              const mm = parseInt(parts[0]);
              const dd = parseInt(parts[1]);
              const year = mm > currentMonth ? currentYear - 1 : currentYear;
              return new Date(year, mm - 1, dd).getDay() === 0;
            });

            return (
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={data.timeseries_data}>
                  <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    stroke="#888"
                    tick={{ fontSize: 11, fill: '#888' }}
                    tickFormatter={xTickFormatter}
                    interval={xInterval}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke={leftMetric?.color || '#fff'}
                    tick={{ fontSize: 11 }}
                    label={{ value: leftMetric?.name || '', angle: -90, position: 'insideLeft', style: { fill: leftMetric?.color || '#fff', fontSize: 10 } }}
                  />
                  {rightMetric && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke={rightMetric.color}
                      tick={{ fontSize: 11 }}
                      label={{ value: rightMetric.name, angle: 90, position: 'insideRight', style: { fill: rightMetric.color, fontSize: 10 } }}
                    />
                  )}
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a2e', border: '1px solid #00ffcc',
                      borderRadius: '8px', fontFamily: 'Courier New, monospace', fontSize: '0.8rem'
                    }}
                    labelFormatter={(label: string) => {
                      const parts = label.split('-');
                      const mm = parseInt(parts[0]);
                      const dd = parseInt(parts[1]);
                      return `📅 ${dd} de ${monthNamesFull[mm]}`;
                    }}
                    formatter={(value: number, name: string) => {
                      const m = (data.available_metrics || []).find(am => am.id === name);
                      const label = m?.name || name;
                      if (name === 'spend' || name === 'cpm' || name === 'cpl') return [`R$ ${value.toFixed(2)}`, label];
                      if (name === 'ctr' || name === 'engagement_rate') return [`${value.toFixed(2)}%`, label];
                      return [value.toLocaleString('pt-BR'), label];
                    }}
                  />
                  <Legend />
                  {/* Marcadores de domingo */}
                  {sundayDates.map((d: string) => (
                    <ReferenceLine
                      key={`sun-${d}`}
                      x={d}
                      yAxisId="left"
                      stroke="#ffffff"
                      strokeOpacity={0.18}
                      strokeWidth={16}
                      label={{ value: 'Dom', position: 'insideTop', offset: 6, fontSize: 9, fill: '#777', fontFamily: 'monospace' }}
                    />
                  ))}
                  {selectedMetrics.map((metricId, idx) => {
                    const m = metricsMap[metricId];
                    if (!m) return null;
                    const yId = idx === 0 ? 'left' : (idx === 1 && rightMetric ? 'right' : 'left');
                    if (idx < 2) {
                      return (
                        <Line
                          key={metricId}
                          yAxisId={yId}
                          type="monotone"
                          dataKey={metricId}
                          name={metricId}
                          stroke={m.color}
                          strokeWidth={2.5}
                          connectNulls={true}
                          dot={{ stroke: m.color, strokeWidth: 2, r: 4, fill: '#1e1e1e' }}
                          activeDot={{ r: 6 }}
                        />
                      );
                    }
                    return (
                      <Area
                        key={metricId}
                        yAxisId="left"
                        type="monotone"
                        dataKey={metricId}
                        name={metricId}
                        stroke={m.color}
                        fill={m.color}
                        fillOpacity={0.12}
                        strokeWidth={1.5}
                        connectNulls={true}
                      />
                    );
                  })}
                </ComposedChart>
              </ResponsiveContainer>
            );
          })()}
        </div>
      )}

      {/* Charts Row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
        {/* Temperatura Instagram */}
        <div style={{ background: '#1e1e1e', border: `1px solid ${tempColor}44`, borderRadius: '8px', padding: '1.5rem' }}>
          <h3 style={{ color: '#00ffcc', marginBottom: '0.5rem', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
            🔥 Temperatura Instagram
          </h3>
          <div style={{ color: '#666', fontSize: '0.7rem', marginBottom: '0.5rem' }}>
            Escala 0-100 · Verde=Crescimento · Vermelho=Declínio
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <RadialBarChart cx="50%" cy="70%" innerRadius="60%" outerRadius="90%" data={tempGaugeData} startAngle={180} endAngle={0}>
              <RadialBar
                dataKey="value"
                cornerRadius={10}
                fill={tempColor}
                background={{ fill: '#1a1a1a' }}
              />
            </RadialBarChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: '-1.5rem' }}>
            <span style={{ color: tempColor, fontSize: '2.2rem', fontWeight: 'bold' }}>{temp}</span>
            <span style={{ color: '#666', fontSize: '0.9rem' }}>/100</span>
          </div>
          <div style={{ textAlign: 'center', color: tempColor, fontSize: '0.8rem', fontWeight: 'bold', letterSpacing: '2px', marginTop: '0.3rem' }}>
            {tempLabel}
          </div>
        </div>

        {/* CPM por Conta */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.5rem' }}>
          <h3 style={{ color: '#00ffcc', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
            💰 CPM por Conta
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.ads.cpm_breakdown}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis dataKey="account" stroke="#888" tick={{ fontSize: 8 }} />
              <YAxis stroke="#888" />
              <Tooltip contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #333', fontFamily: 'Courier New' }} />
              <Bar dataKey="cpm" fill="#00ffcc" name="CPM (R$)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Leads por Hora */}
        <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1.5rem' }}>
          <h3 style={{ color: '#00ffcc', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
            📊 Leads por Hora (Hoje - Horário Brasília)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.leads.cpl_by_hour}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis dataKey="hour" stroke="#888" tick={{ fontSize: 9 }} interval={2} />
              <YAxis stroke="#888" allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #333', fontFamily: 'Courier New' }} />
              <Bar dataKey="leads" fill="#ff00ff" name="Leads" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        
      </div>

      

      {/* 🏆 Winning Content */}
        <div style={{ background: '#1e1e1e', border: '1px solid #ffd700', borderRadius: '8px', padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ color: '#ffd700', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '2px' }}>
            🏆 Conteúdo Vencedor
          </h3>
          {data.winning_content ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <div style={{ color: '#ccc', fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '1rem', fontStyle: 'italic' }}>
                  "{data.winning_content.caption || 'Sem caption'}"
                </div>
                <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                  📅 {data.winning_content.date} · {data.winning_content.type || 'Post'}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div style={{ background: '#0d0d0d', borderRadius: '4px', padding: '0.5rem', textAlign: 'center' }}>
                  <div style={{ color: '#ff4444', fontSize: '1.2rem', fontWeight: 'bold' }}>
                    {data.winning_content.reach}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.6rem' }}>ALCANCE</div>
                </div>
                <div style={{ background: '#0d0d0d', borderRadius: '4px', padding: '0.5rem', textAlign: 'center' }}>
                  <div style={{ color: '#ff00ff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                    {data.winning_content.likes}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.6rem' }}>LIKES</div>
                </div>
                <div style={{ background: '#0d0d0d', borderRadius: '4px', padding: '0.5rem', textAlign: 'center' }}>
                  <div style={{ color: '#00ffcc', fontSize: '1.2rem', fontWeight: 'bold' }}>
                    {data.winning_content.comments}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.6rem' }}>COMMENTS</div>
                </div>
                <div style={{ background: '#0d0d0d', borderRadius: '4px', padding: '0.5rem', textAlign: 'center' }}>
                  <div style={{ color: '#ffd700', fontSize: '1.2rem', fontWeight: 'bold' }}>
                    {data.winning_content.saves}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.6rem' }}>SAVES</div>
                </div>
              </div>
              {data.winning_content.permalink && (
                <a href={data.winning_content.permalink} target="_blank" rel="noopener noreferrer"
                  style={{ color: '#3b82f6', fontSize: '0.75rem', marginTop: '0.8rem', textDecoration: 'none' }}>
                  🔗 Ver no Instagram →
                </a>
              )}
            </div>
          ) : (
            <div style={{ color: '#666', fontSize: '0.9rem', textAlign: 'center', marginTop: '2rem' }}>
              Nenhum post encontrado
            </div>
          )}
        </div>

      {/* Footer Stats */}
      <div style={{ background: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '1rem', display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>CPM Médio (7d)</span>
          <span style={{ color: '#00ff41', fontWeight: 'bold' }}>R$ {(data.ads.cpm_avg_7d || 0).toFixed(2)}</span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>Δ CPM vs 7d</span>
          <span style={{ color: (data.ads.cpm_delta_pct || 0) > 0 ? '#ff4444' : '#00ff41', fontWeight: 'bold' }}>
            {(data.ads.cpm_delta_pct || 0) > 0 ? '+' : ''}{(data.ads.cpm_delta_pct || 0).toFixed(1)}%
          </span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>Engagement Rate</span>
          <span style={{ color: '#00ff41', fontWeight: 'bold' }}>{data.instagram.engagement_rate || 0}%</span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>Seguidores</span>
          <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>{(data.instagram.followers_count || 0).toLocaleString('pt-BR')}</span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>Δ Seguidores/dia</span>
          <span style={{ color: (data.instagram.follower_velocity || 0) >= 0 ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
            {(data.instagram.follower_velocity || 0) >= 0 ? '+' : ''}{(data.instagram.follower_velocity || 0).toFixed(1)}
          </span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <span style={{ color: '#888', fontSize: '0.75rem', display: 'block' }}>Stories 7d</span>
          <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>{data.instagram.stories_total_7d || 0}</span>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default WarRoomDashboard;
